/**
 * Generator definitions for the built-in primitives.
 *
 * Each generator wraps the parameterized factory function in `primitives.ts`
 * and exposes it through the common `Generator` abstraction so that the
 * sidebar panel, add menu, and store-side rebuild flow can work uniformly.
 *
 * All generators in this file emit single-mesh outputs. Compound (group)
 * generators (e.g. Room) live in their own files alongside this one.
 *
 * Each generator uses `defineGenerator` so its `build` function is
 * strongly typed over a local `*Params` interface. The runtime boundary
 * between the sidebar's untyped record and these interfaces is handled
 * by the `numParam` / `intParam` coercion readers in `./types.ts`.
 */

import {
  generateCircle,
  generateCone,
  generateCube,
  generateCylinder,
  generateIcosphere,
  generatePlane,
  generateSphere,
  generateTorus,
} from "../primitives.ts";
import type {
  Generator,
  GeneratorOutput,
  GeneratorParamSpec,
  GeneratorParams,
} from "./types.ts";
import {
  defineGenerator,
  intParam,
  numParam,
  paramsFromSpecs,
} from "./types.ts";

function mesh(geometry: ReturnType<typeof generateCube>): GeneratorOutput {
  return { kind: "mesh", geometry };
}

// ---------------------------------------------------------------------------
// Primitive defaults — physically realistic at 1m unit scale
// ---------------------------------------------------------------------------
//
// Every default below is sized against real-world objects so a fresh
// primitive reads as a tangible reference next to the 1.7 m mannequin:
//
//   Cube       1.0 m³                    — wooden crate / shipping box
//   Plane      1.0 × 1.0 m               — small rug / yoga mat
//   Circle     Ø 1.0 m                   — round area rug
//   Sphere     Ø 1.0 m                   — pilates ball / beach ball
//   Icosphere  Ø 1.0 m                   — same
//   Cylinder   Ø 1.0 m × 1.0 m tall      — rain barrel / planter
//   Cone       Ø 0.6 m × 1.0 m tall      — tall traffic cone
//   Torus      Ø 1.0 m, tube Ø 0.2 m     — inflatable pool ring / bike tire
//
// Segmentation counts (32 around, 16 rings, 48/24 for torus) match
// the tessellation budget we already used before this file existed.

// ---------------------------------------------------------------------------
// Cube
// ---------------------------------------------------------------------------

interface CubeParams extends GeneratorParams {
  size: number;
  subdivisions: number;
}

const CUBE_SPECS: readonly GeneratorParamSpec[] = [
  {
    id: "size",
    label: "Size",
    type: "number",
    unit: "meters",
    default: 1,
    min: 0.01,
    max: 100,
    step: 0.01,
  },
  {
    id: "subdivisions",
    label: "Subdivisions",
    type: "number",
    unit: "count",
    default: 0,
    min: 0,
    max: 4,
    step: 1,
  },
];

export const CUBE_GENERATOR: Generator = defineGenerator<CubeParams>({
  id: "cube",
  label: "Cube",
  category: "primitive",
  paramSpecs: CUBE_SPECS,
  defaultParams: paramsFromSpecs(CUBE_SPECS) as CubeParams,
  build: (p) =>
    mesh(
      generateCube({
        size: numParam(p, "size", 1),
        subdivisions: intParam(p, "subdivisions", 0),
      }),
    ),
});

// ---------------------------------------------------------------------------
// Plane
// ---------------------------------------------------------------------------

interface PlaneParams extends GeneratorParams {
  size: number;
  subdivisions: number;
}

const PLANE_SPECS: readonly GeneratorParamSpec[] = [
  {
    id: "size",
    label: "Size",
    type: "number",
    unit: "meters",
    default: 1,
    min: 0.01,
    max: 100,
    step: 0.01,
  },
  {
    id: "subdivisions",
    label: "Subdivisions",
    type: "number",
    unit: "count",
    default: 0,
    min: 0,
    max: 4,
    step: 1,
  },
];

export const PLANE_GENERATOR: Generator = defineGenerator<PlaneParams>({
  id: "plane",
  label: "Plane",
  category: "primitive",
  paramSpecs: PLANE_SPECS,
  defaultParams: paramsFromSpecs(PLANE_SPECS) as PlaneParams,
  build: (p) =>
    mesh(
      generatePlane({
        size: numParam(p, "size", 1),
        subdivisions: intParam(p, "subdivisions", 0),
      }),
    ),
});

// ---------------------------------------------------------------------------
// Circle
// ---------------------------------------------------------------------------

interface CircleParams extends GeneratorParams {
  radius: number;
  segments: number;
}

const CIRCLE_SPECS: readonly GeneratorParamSpec[] = [
  {
    id: "radius",
    label: "Radius",
    type: "number",
    unit: "meters",
    default: 0.5,
    min: 0.01,
    max: 50,
    step: 0.01,
  },
  {
    id: "segments",
    label: "Segments",
    type: "number",
    unit: "count",
    default: 32,
    min: 3,
    max: 256,
    step: 1,
  },
];

export const CIRCLE_GENERATOR: Generator = defineGenerator<CircleParams>({
  id: "circle",
  label: "Circle",
  category: "primitive",
  paramSpecs: CIRCLE_SPECS,
  defaultParams: paramsFromSpecs(CIRCLE_SPECS) as CircleParams,
  build: (p) =>
    mesh(
      generateCircle({
        radius: numParam(p, "radius", 0.5),
        segments: intParam(p, "segments", 32),
      }),
    ),
});

// ---------------------------------------------------------------------------
// Sphere
// ---------------------------------------------------------------------------

interface SphereParams extends GeneratorParams {
  radius: number;
  segments: number;
  rings: number;
}

const SPHERE_SPECS: readonly GeneratorParamSpec[] = [
  {
    id: "radius",
    label: "Radius",
    type: "number",
    unit: "meters",
    default: 0.5,
    min: 0.01,
    max: 50,
    step: 0.01,
  },
  {
    id: "segments",
    label: "Segments",
    type: "number",
    unit: "count",
    default: 32,
    min: 3,
    max: 256,
    step: 1,
  },
  {
    id: "rings",
    label: "Rings",
    type: "number",
    unit: "count",
    default: 16,
    min: 2,
    max: 128,
    step: 1,
  },
];

export const SPHERE_GENERATOR: Generator = defineGenerator<SphereParams>({
  id: "sphere",
  label: "Sphere",
  category: "primitive",
  paramSpecs: SPHERE_SPECS,
  defaultParams: paramsFromSpecs(SPHERE_SPECS) as SphereParams,
  build: (p) =>
    mesh(
      generateSphere({
        radius: numParam(p, "radius", 0.5),
        segments: intParam(p, "segments", 32),
        rings: intParam(p, "rings", 16),
      }),
    ),
});

// ---------------------------------------------------------------------------
// Icosphere
// ---------------------------------------------------------------------------

interface IcosphereParams extends GeneratorParams {
  radius: number;
  subdivisions: number;
}

const ICOSPHERE_SPECS: readonly GeneratorParamSpec[] = [
  {
    id: "radius",
    label: "Radius",
    type: "number",
    unit: "meters",
    default: 0.5,
    min: 0.01,
    max: 50,
    step: 0.01,
  },
  {
    id: "subdivisions",
    label: "Subdivisions",
    type: "number",
    unit: "count",
    default: 2,
    min: 0,
    max: 5,
    step: 1,
  },
];

export const ICOSPHERE_GENERATOR: Generator = defineGenerator<IcosphereParams>({
  id: "icosphere",
  label: "Icosphere",
  category: "primitive",
  paramSpecs: ICOSPHERE_SPECS,
  defaultParams: paramsFromSpecs(ICOSPHERE_SPECS) as IcosphereParams,
  build: (p) =>
    mesh(
      generateIcosphere({
        radius: numParam(p, "radius", 0.5),
        subdivisions: intParam(p, "subdivisions", 2),
      }),
    ),
});

// ---------------------------------------------------------------------------
// Cylinder
// ---------------------------------------------------------------------------

interface CylinderParams extends GeneratorParams {
  radius: number;
  height: number;
  segments: number;
}

const CYLINDER_SPECS: readonly GeneratorParamSpec[] = [
  {
    id: "radius",
    label: "Radius",
    type: "number",
    unit: "meters",
    default: 0.5,
    min: 0.01,
    max: 50,
    step: 0.01,
  },
  {
    id: "height",
    label: "Height",
    type: "number",
    unit: "meters",
    default: 1,
    min: 0.01,
    max: 100,
    step: 0.01,
  },
  {
    id: "segments",
    label: "Segments",
    type: "number",
    unit: "count",
    default: 32,
    min: 3,
    max: 256,
    step: 1,
  },
];

export const CYLINDER_GENERATOR: Generator = defineGenerator<CylinderParams>({
  id: "cylinder",
  label: "Cylinder",
  category: "primitive",
  paramSpecs: CYLINDER_SPECS,
  defaultParams: paramsFromSpecs(CYLINDER_SPECS) as CylinderParams,
  build: (p) =>
    mesh(
      generateCylinder({
        radius: numParam(p, "radius", 0.5),
        height: numParam(p, "height", 1),
        segments: intParam(p, "segments", 32),
      }),
    ),
});

// ---------------------------------------------------------------------------
// Cone
// ---------------------------------------------------------------------------

interface ConeParams extends GeneratorParams {
  radius: number;
  height: number;
  segments: number;
}

// Traffic cone proportions: ~Ø 0.6 m base × 1 m tall. A unit-radius cone
// (0.5 m) paired with a 1 m height reads as squat at 1 m scale.
const CONE_SPECS: readonly GeneratorParamSpec[] = [
  {
    id: "radius",
    label: "Radius",
    type: "number",
    unit: "meters",
    default: 0.3,
    min: 0.01,
    max: 50,
    step: 0.01,
  },
  {
    id: "height",
    label: "Height",
    type: "number",
    unit: "meters",
    default: 1,
    min: 0.01,
    max: 100,
    step: 0.01,
  },
  {
    id: "segments",
    label: "Segments",
    type: "number",
    unit: "count",
    default: 32,
    min: 3,
    max: 256,
    step: 1,
  },
];

export const CONE_GENERATOR: Generator = defineGenerator<ConeParams>({
  id: "cone",
  label: "Cone",
  category: "primitive",
  paramSpecs: CONE_SPECS,
  defaultParams: paramsFromSpecs(CONE_SPECS) as ConeParams,
  build: (p) =>
    mesh(
      generateCone({
        radius: numParam(p, "radius", 0.3),
        height: numParam(p, "height", 1),
        segments: intParam(p, "segments", 32),
      }),
    ),
});

// ---------------------------------------------------------------------------
// Torus
// ---------------------------------------------------------------------------

interface TorusParams extends GeneratorParams {
  majorRadius: number;
  minorRadius: number;
  majorSegments: number;
  minorSegments: number;
}

// Pool float / bike tire proportions: Ø 1 m outer with a Ø 0.2 m tube.
// A 0.15 m minor radius gave a 0.3 m tube, visibly chunkier than the
// real objects it evokes at 1 m scale.
const TORUS_SPECS: readonly GeneratorParamSpec[] = [
  {
    id: "majorRadius",
    label: "Major Radius",
    type: "number",
    unit: "meters",
    default: 0.4,
    min: 0.01,
    max: 1,
    step: 0.01,
  },
  {
    id: "minorRadius",
    label: "Minor Radius",
    type: "number",
    unit: "meters",
    default: 0.1,
    min: 0.01,
    max: 0.3,
    step: 0.01,
  },
  {
    id: "majorSegments",
    label: "Major Segments",
    type: "number",
    unit: "count",
    default: 48,
    min: 3,
    max: 96,
    step: 1,
  },
  {
    id: "minorSegments",
    label: "Minor Segments",
    type: "number",
    unit: "count",
    default: 24,
    min: 3,
    max: 48,
    step: 1,
  },
];

export const TORUS_GENERATOR: Generator = defineGenerator<TorusParams>({
  id: "torus",
  label: "Torus",
  category: "primitive",
  paramSpecs: TORUS_SPECS,
  defaultParams: paramsFromSpecs(TORUS_SPECS) as TorusParams,
  build: (p) =>
    mesh(
      generateTorus({
        majorRadius: numParam(p, "majorRadius", 0.4),
        minorRadius: numParam(p, "minorRadius", 0.1),
        majorSegments: intParam(p, "majorSegments", 48),
        minorSegments: intParam(p, "minorSegments", 24),
      }),
    ),
});
